import { DESKTOP_DEVICE_FORM_SETTINGS } from "Constants";
import { ToolTip } from "elements";
import { ProtectedLink } from "TsRoleGuards";
import { useTranslation } from "utilities/i18n";
import { SettingsCategory } from "../../Settings-Shared/DeviceSettingsComponents";
import { SoftwareVersionField } from "../../Settings/ProvisionedUpdateSoftware";
import { DesktopDeviceFormSetting } from "../DesktopDeviceSettings";

export const capabilityCategories = [
  "policy",
  "software_update",
  "video",
  "wireless",
  "general",
  "call_control",
  "audio",
  "com",
  "poly"
];

export type CategorySettingsProps = {
  CategorySetting: ({
    accessor,
    options,
    tooltipText,
    type
  }: DesktopDeviceFormSetting) => JSX.Element;
};

export const LanguageSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("LANGUAGE")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.LANGUAGE.map(setting => (
          <CategorySetting key={setting.accessor} {...setting} />
        ))}
      </div>
    </SettingsCategory>
  );
};

export const GeneralSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("GENERAL")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.GENERAL.map((setting, i) => (
          <CategorySetting key={setting.accessor} {...setting} index={i} />
        ))}
      </div>
    </SettingsCategory>
  );
};

export const RingtonesVolumeSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("RINGTONES_AND_VOLUME")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.RINGTONES_AND_VOLUME.map((setting, i) => (
          <CategorySetting key={setting.accessor} {...setting} index={i} />
        ))}
      </div>
    </SettingsCategory>
  );
};

export const AudioSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("AUDIO")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.AUDIO.map((setting, i) => (
          <CategorySetting key={setting.accessor} {...setting} index={i} />
        ))}
      </div>
    </SettingsCategory>
  );
};

export const SensorsPresenceSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("SENSORS_AND_PRESENCE")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.SENSORS_AND_PRESENCE.map((setting, i) => (
          <CategorySetting key={setting.accessor} {...setting} index={i} />
        ))}
      </div>
    </SettingsCategory>
  );
};

export const SoftphonesMediaPlayersSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("SOFTPHONES_AND_MEDIA_PLAYERS")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.SOFTPHONES_AND_MEDIA_PLAYERS.map(
          (setting, i) => (
            <CategorySetting key={setting.accessor} {...setting} index={i} />
          )
        )}
      </div>
    </SettingsCategory>
  );
};

export const WirelessSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("WIRELESS")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.WIRELESS.map((setting, i) => (
          <CategorySetting key={setting.accessor} {...setting} index={i} />
        ))}
      </div>
    </SettingsCategory>
  );
};

export const AdvancedSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("SETTINGS_ADVANCED")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.ADVANCED.map((setting, i) => (
          <CategorySetting key={setting.accessor} {...setting} index={i} />
        ))}
      </div>
    </SettingsCategory>
  );
};

export const VideoControlsSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("VIDEO_CONTROLS")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.VIDEO_CONTROLS.map((setting, i) => (
          <CategorySetting key={setting.accessor} {...setting} index={i} />
        ))}
      </div>
    </SettingsCategory>
  );
};

export const DiagnosticsSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("DIAGNOSTICS")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.DIAGNOSTICS.map((setting, i) => (
          <CategorySetting key={setting.accessor} {...setting} index={i} />
        ))}
      </div>
    </SettingsCategory>
  );
};

export const ResetDeviceSettings: React.FC = () => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("RESET_DEVICE")}>
      <div className="details grid form">
        <div className="form-group">
          <label className="col-4 text-right" htmlFor={"RESTORE_DEFAULTS"}>
            <ToolTip icon="help_circle" iconTitle="help" position="bottom">
              {t("RESTORE_DEFAULTS_DESC")}
            </ToolTip>
            {t("RESTORE_DEFAULTS")}:
          </label>
          <div className="col-4">
            <ProtectedLink
              nest
              to={{ pathname: "/confirm" }}
              data-testid="restore-default-settings"
              className="btn btn-primary"
              requirement="update:device"
            >
              {t("RESTORE_DEFAULTS")}
            </ProtectedLink>
          </div>
        </div>
      </div>
    </SettingsCategory>
  );
};

const DesktopDeviceLatestVersionLabel = ({
  options
}: Pick<DesktopDeviceFormSetting, "options">) => {
  // The latest version is always the first in the list
  return (
    <>
      {options?.length > 0 && (
        <SoftwareVersionField
          softwareVersion={options[0].text}
          label="LATEST_VERSION"
          className="latest-version"
        />
      )}
    </>
  );
};

export const SoftwareUpdateSettings: React.FC<CategorySettingsProps> = ({
  CategorySetting
}) => {
  const { t } = useTranslation();
  return (
    <SettingsCategory title={t("SOFTWARE_UPDATE")}>
      <div className="details grid form">
        {DESKTOP_DEVICE_FORM_SETTINGS.SOFTWARE_UPDATE.map((setting, i) => (
          <CategorySetting
            key={setting.accessor}
            {...setting}
            index={i}
            WhenHidden={
              setting.accessor === "software_update.policy.version"
                ? DesktopDeviceLatestVersionLabel
                : undefined
            }
          />
        ))}
      </div>
    </SettingsCategory>
  );
};
